/**
 * HTTP, URL, email, domain, and IP address validation utilities.
 * Provides validation functions for form fields containing network addresses.
 */

// Declare external dependencies

// Interface for form element wrapper context
interface ValidationContext {
  element: HTMLInputElement;
  get: () => string;
  set: (value: string) => void;
  setMessage: (message: string | null) => boolean;
  allowDuplicates?: boolean;
  allowScripts?: boolean;
  allowRelative?: boolean;
  allowMask?: boolean;
}

/**
 * Validates email using HTML5 validation API
 */
function validateEmail(this: ValidationContext): boolean {
  const msg = this.element.validity.valid === false ? this.element.validationMessage : "";
  return this.setMessage(msg);
}

const validateEmailList = validateEmail;

/**
 * Validates a single domain name or IP address
 */
function validateDomain(this: ValidationContext): boolean {
  this.set(strip(this.get(), " \r\n\t"));
  return this.setMessage(checkDomain(this.get(), false) ? "" : this.get() + " is not a valid domain or IP address");
}

/**
 * Validates a list of domain names separated by common delimiters
 */
function validateDomainList(this: ValidationContext): boolean {
  const tokens = tokenize(this.get(), " ,;|\n\r\t");
  let out = "";
  const domainSet: Record<string, boolean> = {};

  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i] > "") {
      if (!checkDomain(tokens[i], false)) {
        return this.setMessage('"' + tokens[i] + '" is not a valid domain name or IP address');
      }
      if (this.allowDuplicates || !domainSet[tokens[i]]) {
        domainSet[tokens[i]] = true;
        out += ", " + tokens[i];
      }
    }
  }
  this.set(out.substring(2));
  return out > "";
}

const defaultProtocol = "http://";

/**
 * Validates a URL, automatically adding protocol if missing
 */
function validateURL(this: ValidationContext): boolean {
  let url = strip(this.get(), " \r\n\t");

  if (url.toLowerCase().indexOf("mailto:") === 0) {
    const message = checkEmail(url.substring(7));
    return message === "" ? true : this.setMessage("Bad mailto link: " + message);
  }

  if (url.toLowerCase().indexOf("javascript:") === 0) {
    return this.allowScripts ? true : this.setMessage("JavaScript is not allowed.");
  }

  let protocolEnd = url.indexOf("//") + 2;
  let protocol = defaultProtocol;

  if (this.allowRelative && url.charAt(0) === "/") return true;

  if (protocolEnd < 2 || protocolEnd > 8) {
    url = protocol + url;
    protocolEnd = protocol.length;
  } else {
    protocol = url.substring(0, protocolEnd);
  }

  const domainEnd = url.indexOf("/", protocolEnd);
  const domain = url.substring(protocolEnd, domainEnd === -1 ? url.length : domainEnd);

  if (!checkDomain(domain, false)) {
    return this.setMessage("Invalid domain: " + domain);
  }

  this.set(url);
  return true;
}

/**
 * Validates an email address format
 * @param e - The email address to validate
 * @returns Empty string if valid, error message if invalid
 */
function checkEmail(e: string): string {
  const quoted = e.length > 3 && e.charAt(0) === '"';
  const validChars = ALPHANUMERIC + ".@!$&*-=^`|~#%'+/?_{}";

  let atSign = quoted ? e.indexOf('"@', 1) : e.indexOf('@');

  if (atSign === -1) {
    return quoted
      ? "Email addresses with a quoted local part must have an @ immediately after closing the quote"
      : "Email addresses must contain an @";
  }

  if (quoted) atSign++;

  const before = e.substring(0, atSign);
  const after = e.substring(atSign + 1, e.length);

  if (before.length === (quoted ? 2 : 0)) {
    return "@ cannot be the first character in an email address.";
  }
  if (after.length === 0) {
    return "@ cannot be the last character in an email address.";
  }
  if (after.indexOf("@") !== -1) {
    return "Email address cannot contain two @ characters.";
  }
  if (!checkDomain(after, false)) {
    return "Invalid email domain: " + after;
  }
  if (!quoted && e !== extract(e, validChars)) {
    return "Email address contains invalid characters.";
  }

  return "";
}

const MAX_PORT = (1 << 16) - 1;
const MAX_IP_SECTION = (1 << 8) - 1;

/**
 * Validates a domain name or IP address
 * @param domain - The domain or IP to validate
 * @param requireSubDomain - Whether to require a subdomain (3+ sections)
 * @returns true if valid, false otherwise
 */
function checkDomain(domain: string, requireSubDomain: boolean): boolean {
  const minSections = requireSubDomain ? 3 : 2;
  let isIP = true;
  const sections: string[] = [];
  let curSection = 0;
  let hasPort = false;
  let port = "";

  sections[curSection] = "";

  for (let i = 0; i < domain.length; i++) {
    const c = domain.charAt(i);

    if (c === '.') {
      if (hasPort) return false;
      sections[++curSection] = "";
    } else if (ALPHA.indexOf(c) !== -1 || c === '-') {
      if (hasPort) return false;
      isIP = false;
      sections[curSection] += c;
    } else if (NUMERIC.indexOf(c) !== -1) {
      if (hasPort) {
        port += c;
      } else {
        sections[curSection] += c;
      }
    } else if (c === ":") {
      if (hasPort) return false;
      hasPort = true;
      port = "";
    } else {
      return false;
    }
  }

  if (isIP) {
    if (curSection !== 3) return false;
    for (let i = 0; i <= curSection; i++) {
      if (sections[i] === "" || parseInt(sections[i]) > MAX_IP_SECTION) return false;
    }
  } else {
    if (curSection + 1 < minSections) return false;
    for (let i = 0; i <= curSection; i++) {
      const minLen = (i === curSection) ? 2 : 1;
      const maxLen = (i === curSection) ? 7 : 63;
      if (sections[i].length < minLen || sections[i].length > maxLen) return false;
    }
  }

  if (hasPort && (port === "" || parseInt(port) > MAX_PORT)) return false;

  return true;
}

/**
 * Splits a string into tokens by separator characters
 * @param str - The string to tokenize
 * @param separators - Characters to use as separators
 * @returns Array of tokens
 */
function tokenize(str: string, separators: string): string[] {
  const tokens: string[] = [];
  let curToken = 0;

  tokens[curToken] = "";

  for (let i = 0; i < str.length; i++) {
    if (separators.indexOf(str.charAt(i)) !== -1) {
      if (tokens[curToken] !== "") {
        tokens[++curToken] = "";
      }
    } else {
      tokens[curToken] += str.charAt(i);
    }
  }

  return tokens;
}

/**
 * Validates a list of IP addresses (IPv4 or IPv6) with optional CIDR masks
 */
function validateIPList(this: ValidationContext): boolean {
  const ips = this.get();
  const ipList = ips.split('\n');
  let error: string | null = null;

  for (let i = 0; i < ipList.length; i++) {
    let ip = ipList[i].replace(/^\s+|\s+$/g, '');

    if (ip > "") {
      const slash = ip.indexOf("/");
      let mask = "";

      if (slash !== -1) {
        mask = extract(ip.substring(slash + 1), NUMERIC);
        ip = ip.substring(0, slash);
      }

      let isIPv6 = false;

      if (ip.indexOf(":") !== -1) {
        isIPv6 = true;
        ip = extract(ip, NUMERIC + "ABCDEFabcdef:");
        const parts = ip.split(":");
        let foundAbbrv = false;

        for (let j = 0; j < parts.length; j++) {
          if (parts[j].length > 4) {
            error = "Each part of a IPv6 address may contain, at most, 4 hexidecimal digits.";
            break;
          } else if (parts[j].length === 0) {
            if (j === 0 && parts[1] > "") {
              error = "An IPv6 address may not begin with a single colon.";
              break;
            } else if (j === parts.length - 1 && parts[j - 1] > "") {
              error = "An IPv6 address may not end with a single colon.";
              break;
            } else if (foundAbbrv) {
              error = "An IPv6 address may contain only one :: shortcut syntax.";
              break;
            } else if (j > 0) {
              foundAbbrv = true;
            }
          }
        }

        if (error === null && (foundAbbrv === false && parts.length < 8 || parts.length > 8)) {
          error = "An IPv6 address must have 8 parts.";
        }
      } else {
        ip = extract(ip, NUMERIC + ".");
        const parts = ip.split(".");

        if (parts.length !== 4) {
          error = "An IPv4 address must have exactly 4 parts.";
        } else {
          for (let j = 0; j < parts.length; j++) {
            if (parts[j] <= "") {
              error = "An IPv4 address part may not be empty.";
              break;
            }
            const num = parseInt(parts[j], 10);
            if (num > 255) {
              error = "An IPv4 address part may not be greater than 255.";
              break;
            }
            parts[j] = "" + num;
          }
          ip = parts.join(".");
        }
      }

      if (this.allowMask !== false) {
        const max = isIPv6 ? 128 : 32;
        let num = max;

        if (mask > "") {
          num = parseInt(mask, 10);
          if (error === null && num > max) {
            error = isIPv6
              ? "An IPv6 mask may not be greater than 128"
              : "The mask for a IPv4 address by not be greater than 32.";
          }
        }
        ip += "/" + num;
      }
    }

    if (ip > "") {
      ipList[i] = ip;
    } else {
      ipList.splice(i--, 1);
    }

    if (error !== null) break;
  }

  this.set(ipList.join("\n"));
  return this.setMessage(error);
}

// Export for module usage
export {
  validateEmail,
  validateEmailList,
  validateDomain,
  validateDomainList,
  validateURL,
  validateIPList,
  checkEmail,
  checkDomain,
  tokenize,
  defaultProtocol,
  MAX_PORT,
  MAX_IP_SECTION
};

// Expose on globalThis for backwards compatibility
(globalThis as any).validateEmail = validateEmail;
(globalThis as any).validateEmailList = validateEmailList;
(globalThis as any).validateDomain = validateDomain;
(globalThis as any).validateDomainList = validateDomainList;
(globalThis as any).validateURL = validateURL;
(globalThis as any).validateIPList = validateIPList;
(globalThis as any).checkEmail = checkEmail;
(globalThis as any).checkDomain = checkDomain;
(globalThis as any).tokenize = tokenize;
(globalThis as any).defaultProtocol = defaultProtocol;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("validatehttp.js");
}
